import { useQuery } from '@tanstack/react-query';
import { n8nFetch } from '../n8nClient';
import type {
  Empresa,
  Contato,
  PreAnalise,
  EmpresaServico,
  Endereco,
  Oportunidade,
  Evento,
  EventoListResponse,
  ProspectCadence,
} from '../types';

export function useEmpresa(id: string) {
  return useQuery({
    queryKey: ['empresa', id],
    queryFn: () => n8nFetch<Empresa>(`/empresa-detail?id=${id}`),
    enabled: !!id,
  });
}

export function useContatos(empresaId: string) {
  return useQuery({
    queryKey: ['contatos', empresaId],
    queryFn: () => n8nFetch<Contato[]>(`/empresa-sub?id=${empresaId}&sub=contatos`),
    enabled: !!empresaId,
  });
}

export function useEnderecos(empresaId: string) {
  return useQuery({
    queryKey: ['enderecos', empresaId],
    queryFn: () => n8nFetch<Endereco[]>(`/empresa-sub?id=${empresaId}&sub=enderecos`),
    enabled: !!empresaId,
  });
}

export function usePreAnalises(empresaId: string) {
  return useQuery({
    queryKey: ['pre_analises', empresaId],
    queryFn: () => n8nFetch<PreAnalise[]>(`/empresa-sub?id=${empresaId}&sub=pre-analises`),
    enabled: !!empresaId,
  });
}

/**
 * Wrapper sobre usePreAnalises: pega a versao mais recente (data ordenada DESC por versao_n).
 */
export function usePreAnaliseLatest(empresaId: string) {
  const query = usePreAnalises(empresaId);
  const latest: PreAnalise | null =
    query.data && query.data.length > 0 ? (query.data[0] ?? null) : null;
  return { ...query, data: latest };
}

// Backend (crm-read v2) retorna empresa_servico flat com prefixo servico_*.
// Reshape pro tipo EmpresaServico que o frontend espera.
interface EmpresaServicoFlat {
  id: string;
  fit_score: number;
  prioridade: 'alta' | 'media' | 'baixa';
  justificativa?: string;
  manual_ou_auto: 'manual' | 'auto';
  servico_id: string;
  servico_slug: string;
  servico_nome: string;
  servico_descricao_curta?: string;
  servico_ticket_tipico_brl?: number;
}

export function useEmpresaServicos(empresaId: string) {
  return useQuery({
    queryKey: ['empresa_servicos', empresaId],
    queryFn: async (): Promise<EmpresaServico[]> => {
      const flat = await n8nFetch<EmpresaServicoFlat[]>(`/empresa-sub?id=${empresaId}&sub=servicos`);
      return flat.map((f) => ({
        id: f.id,
        fit_score: f.fit_score,
        prioridade: f.prioridade,
        justificativa: f.justificativa,
        manual_ou_auto: f.manual_ou_auto,
        servico: {
          id: f.servico_id,
          slug: f.servico_slug,
          nome: f.servico_nome,
          descricao_curta: f.servico_descricao_curta,
          ticket_tipico_brl: f.servico_ticket_tipico_brl,
        },
      }));
    },
    enabled: !!empresaId,
  });
}

// Backend v2 retorna oportunidade flat com prefixos estagio_* e servico_*.
interface OportunidadeFlat {
  id: string;
  valor_estimado_mensal_brl?: number;
  valor_vazamento_quantificado_brl_mes?: number;
  probabilidade?: number;
  data_proxima_acao?: string;
  acao_descricao?: string;
  proximo_movimento_recomendado?: string;
  motivo_perdido?: string;
  data_fechamento?: string;
  estagio_id: number;
  estagio_nome: string;
  estagio_cor: string;
  estagio_ordem: number;
  servico_id?: string | null;
  servico_nome?: string | null;
}

export function useOportunidades(empresaId: string) {
  return useQuery({
    queryKey: ['oportunidades', empresaId],
    queryFn: async (): Promise<Oportunidade[]> => {
      const flat = await n8nFetch<OportunidadeFlat[]>(`/empresa-sub?id=${empresaId}&sub=oportunidades`);
      return flat.map((f) => ({
        id: f.id,
        valor_estimado_mensal_brl: f.valor_estimado_mensal_brl,
        valor_vazamento_quantificado_brl_mes: f.valor_vazamento_quantificado_brl_mes,
        probabilidade: f.probabilidade,
        data_proxima_acao: f.data_proxima_acao,
        acao_descricao: f.acao_descricao,
        proximo_movimento_recomendado: f.proximo_movimento_recomendado,
        motivo_perdido: f.motivo_perdido,
        data_fechamento: f.data_fechamento,
        estagio: {
          id: f.estagio_id,
          nome: f.estagio_nome,
          cor: f.estagio_cor,
          ordem: f.estagio_ordem,
        },
        servico:
          f.servico_id && f.servico_nome ? { id: f.servico_id, nome: f.servico_nome } : undefined,
      }));
    },
    enabled: !!empresaId,
  });
}

export function useEventos(empresaId: string, limit = 50) {
  return useQuery({
    queryKey: ['eventos', empresaId, limit],
    queryFn: async (): Promise<EventoListResponse> => {
      const items = await n8nFetch<Evento[]>(`/empresa-sub?id=${empresaId}&sub=eventos&limit=${limit}`);
      return { total: items.length, items };
    },
    enabled: !!empresaId,
  });
}

export function useCadencia(empresaId: string) {
  return useQuery({
    queryKey: ['cadencia', empresaId],
    queryFn: async (): Promise<ProspectCadence | null> => {
      const res = await n8nFetch<ProspectCadence | Record<string, never> | null>(
        `/empresa-sub?id=${empresaId}&sub=cadencia`
      );
      if (!res || Object.keys(res).length === 0) return null;
      return res as ProspectCadence;
    },
    enabled: !!empresaId,
  });
}
